import type { LyricsSettings } from './lyricsSettings';
import {
  buildAnupallaviRegex,
  buildCaranamRegex,
  buildPallaviRegex,
  buildSectionRegex,
} from './sectionWords';

const SVARA_PREFIX_RE = /^\s*s\s*:\s/i;
const LINE_NUMBER_TAIL = /…\s*([0-9०-९]{1,3})\s*$/;

export type LineKind = 'section' | 'svara' | 'lyric' | 'blank';

export interface LyricLine {
  kind: LineKind;
  text: string;
  number?: number;
  index: number;
  /** True on every line of a pallavi clone, including its heading line. */
  repeat?: boolean;
}

export interface Stanza {
  /** kind of section heading that opens this stanza, or null if it's pre-amble */
  heading: { text: string; index: number } | null;
  kind: 'pallavi' | 'anupallavi' | 'caranam' | 'other';
  /** all the lines (lyric + svara + blank) inside this stanza, in document order */
  lines: LyricLine[];
  /** True on the clones `transformRepeatPallavi` inserts. */
  isRepeat?: boolean;
}

/**
 * Clone line indices start here so they can never collide with a source line index.
 * `activeLineIndex` keys off `index`, so a collision would point scroll-sync at two
 * different rendered lines.
 */
export const REPEAT_INDEX_FLOOR = 1_000_000;

export function classifyLines(raw: string, languageCode: string = 'en'): LyricLine[] {
  const sectionRe = buildSectionRegex(languageCode);
  const lines = raw.split(/\r?\n/);
  return lines.map((rawLine, i) => {
    const line = rawLine.trim();
    if (!line) return { kind: 'blank', text: '', index: i };
    if (sectionRe.test(line)) return { kind: 'section', text: line.replace(/[:：]\s*$/, ''), index: i };
    if (SVARA_PREFIX_RE.test(rawLine)) return { kind: 'svara', text: rawLine.replace(SVARA_PREFIX_RE, ''), index: i };
    const tail = line.match(LINE_NUMBER_TAIL);
    if (tail) {
      const num = tail[1].replace(/[०-९]/g, (c) => '0123456789'['०१२३४५६७८९'.indexOf(c)]);
      return {
        kind: 'lyric',
        text: line.replace(LINE_NUMBER_TAIL, '').trim(),
        number: parseInt(num, 10),
        index: i,
      };
    }
    return { kind: 'lyric', text: line, index: i };
  });
}

/** Group classified lines into stanzas keyed by their section heading. */
export function groupStanzas(lines: LyricLine[], languageCode: string = 'en'): Stanza[] {
  const anupallaviRe = buildAnupallaviRegex(languageCode);
  const pallaviRe = buildPallaviRegex(languageCode);
  const caranamRe = buildCaranamRegex(languageCode);

  const out: Stanza[] = [];
  let current: Stanza = { heading: null, kind: 'other', lines: [] };
  for (const l of lines) {
    if (l.kind === 'section') {
      // commit current
      if (current.lines.length > 0 || current.heading) out.push(current);
      // Anupallavi is tested FIRST: `pallavi` is a substring of `anupallavi` in
      // every script, so the other order classifies every anupallavi as a pallavi.
      const kind: Stanza['kind'] = anupallaviRe.test(l.text)
        ? 'anupallavi'
        : pallaviRe.test(l.text)
          ? 'pallavi'
          : caranamRe.test(l.text)
            ? 'caranam'
            : 'other';
      current = { heading: { text: l.text, index: l.index }, kind, lines: [] };
    } else {
      current.lines.push(l);
    }
  }
  if (current.lines.length > 0 || current.heading) out.push(current);
  return out;
}

/** Trailing caraṇam-end marker: two-or-more dots OR an ellipsis, then a number. */
const CARANAM_END_RE = /(\.{2,}\s*\d+|…\s*\d+)\s*$/;

/**
 * Split a caraṇam's lines into verses at blank lines AND at `...N` / `…N` end
 * markers. Both conventions appear in the catalog.
 *
 * jnana-native gets the blank-line half for free — its parser commits a stanza at
 * every blank line — but web keeps blanks as members of the stanza, so both splits
 * have to happen here for the two platforms to agree.
 */
function splitCaranamVerses(lines: LyricLine[]): LyricLine[][] {
  const verses: LyricLine[][] = [];
  let current: LyricLine[] = [];

  for (const l of lines) {
    if (l.kind === 'blank') {
      if (current.length) {
        verses.push(current);
        current = [];
      }
      continue;
    }
    current.push(l);
    const endsVerse = l.kind === 'lyric' && (l.number != null || CARANAM_END_RE.test(l.text));
    if (endsVerse) {
      verses.push(current);
      current = [];
    }
  }

  if (current.length) verses.push(current);
  return verses;
}

/**
 * Bring the pallavi back everywhere singers actually return to it: once after the
 * anupallavi, and after every caraṇam verse.
 *
 * Clones are tagged `isRepeat` (and their lines `repeat`) so the renderer can rail
 * and label them, and carry synthetic indices above REPEAT_INDEX_FLOOR so no two
 * rendered lines ever share an index.
 */
export function transformRepeatPallavi(
  stanzas: Stanza[],
  repeatsSvaras: boolean = false
): Stanza[] {
  const pallavi = stanzas.find((s) => s.kind === 'pallavi' && !s.isRepeat);
  if (!pallavi) return stanzas;

  let synthetic = REPEAT_INDEX_FLOOR;
  /**
   * By default the refrain carries the lyric only — the pallavi's notation is already
   * printed in full above it, so repeating it after every verse is noise.
   *
   * `repeatsSvaras` follows the "fill missing svaras" setting: someone reading the
   * notation as they sing wants it on the refrain too, rather than scrolling back.
   */
  const refrain = (): Stanza => ({
    heading: pallavi.heading ? { text: pallavi.heading.text, index: synthetic++ } : null,
    kind: 'pallavi',
    lines: pallavi.lines
      .filter((l) => repeatsSvaras || l.kind !== 'svara')
      .map((l) => ({ ...l, index: synthetic++, repeat: true })),
    isRepeat: true,
  });

  const out: Stanza[] = [];
  for (const s of stanzas) {
    if (s.isRepeat) {
      // An already-inserted refrain: keep it, but never clone from it.
      out.push(s);
      continue;
    }
    if (s.kind === 'anupallavi') {
      out.push(s, refrain());
      continue;
    }
    if (s.kind !== 'caranam') {
      out.push(s);
      continue;
    }

    const verses = splitCaranamVerses(s.lines);
    if (verses.length === 0) {
      out.push(s, refrain());
      continue;
    }
    verses.forEach((verse, i) => {
      // The caraṇam heading rides on the first verse only.
      out.push({ heading: i === 0 ? s.heading : null, kind: 'caranam', lines: verse });
      out.push(refrain());
    });
  }

  /**
   * Collapse any run of adjacent refrains. Re-running this over already-transformed
   * stanzas must be a no-op rather than a doubling, and two refrains in a row is
   * never musically meaningful anyway.
   */
  return out.filter((s, i) => !(s.isRepeat && out[i - 1]?.isRepeat));
}

/** Split a stanza's lines into verses (runs of non-blank lines separated by blanks). */
function splitVerses(lines: LyricLine[]): LyricLine[][] {
  const verses: LyricLine[][] = [];
  let cur: LyricLine[] = [];
  for (const l of lines) {
    if (l.kind === 'blank') {
      if (cur.length) { verses.push(cur); cur = []; }
    } else {
      cur.push(l);
    }
  }
  if (cur.length) verses.push(cur);
  return verses;
}

/**
 * Fill missing caranam svaras. Bhajans repeat the same melody across caranam
 * verses but usually notate it only on the first verse — which may sit under a
 * single `caraṇaṁ` heading (one stanza, many verses) OR under repeated headings
 * (many stanzas). Both cases are handled at the VERSE level: take the first
 * caranam verse that has svaras as the template, then for every later caranam
 * verse that has lyric lines but no svaras, interleave the template svaras.
 */
export function transformInterpolateSvaras(stanzas: Stanza[]): Stanza[] {
  let templateSvaras: LyricLine[] | null = null;
  for (const s of stanzas) {
    if (s.kind !== 'caranam') continue;
    for (const verse of splitVerses(s.lines)) {
      const svaras = verse.filter((l) => l.kind === 'svara');
      if (svaras.length > 0) { templateSvaras = svaras; break; }
    }
    if (templateSvaras) break;
  }
  if (!templateSvaras || templateSvaras.length === 0) return stanzas;
  const tpl = templateSvaras;

  let idx = 2_000_000; // synthetic indices for filled svaras + re-inserted blanks
  return stanzas.map((s) => {
    if (s.kind !== 'caranam') return s;
    const verses = splitVerses(s.lines);
    const newLines: LyricLine[] = [];
    verses.forEach((verse, vi) => {
      if (vi > 0) newLines.push({ kind: 'blank', text: '', index: idx++ });
      const hasSvara = verse.some((l) => l.kind === 'svara');
      const lyricLines = verse.filter((l) => l.kind === 'lyric');
      if (hasSvara || lyricLines.length === 0) {
        newLines.push(...verse);
      } else {
        for (let i = 0; i < lyricLines.length; i++) {
          if (tpl[i]) newLines.push({ ...tpl[i], index: idx++ });
          newLines.push(lyricLines[i]);
        }
      }
    });
    return { ...s, lines: newLines };
  });
}

export function stripSvaras(stanzas: Stanza[]): Stanza[] {
  return stanzas.map((s) => ({ ...s, lines: s.lines.filter((l) => l.kind !== 'svara') }));
}

/** Full lyrics pipeline: raw text + user settings → ordered renderable lines. */
export function buildFinalLines(
  text: string,
  settings?: LyricsSettings,
  languageCode: string = 'en'
): LyricLine[] {
  const classified = classifyLines(text, languageCode);
  if (classified.length === 0) return [];
  let stanzas = groupStanzas(classified, languageCode);
  if (!settings?.showSvaras) stanzas = stripSvaras(stanzas);
  if (settings?.repeatPallavi) {
    // The refrain repeats its notation only when the reader has asked for svaras to
    // be filled in — and never when svaras are hidden outright.
    stanzas = transformRepeatPallavi(
      stanzas,
      Boolean(settings?.showSvaras && settings?.interpolateSvaras)
    );
  }
  if (settings?.showSvaras && settings?.interpolateSvaras) {
    stanzas = transformInterpolateSvaras(stanzas);
  }
  const flat: LyricLine[] = [];
  for (const s of stanzas) {
    if (s.heading) {
      flat.push({
        kind: 'section',
        text: s.heading.text,
        index: s.heading.index,
        ...(s.isRepeat ? { repeat: true } : {}),
      });
    }
    flat.push(...s.lines);
  }
  return flat;
}
